import React, {useEffect, useState} from 'react';
import {useService} from '@dishtech-react/hooks';
import {GetRippleRoles, areRolesAuthorized} from '@dishtech-react/utils';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {Autocomplete} from '@material-ui/lab';
import {useSelector, useDispatch} from 'react-redux';
import MUIDataTable from 'mui-datatables';
import Add from '@material-ui/icons/Add';
import Delete from '@material-ui/icons/Delete';
import Select from '@material-ui/icons/Check';
import PropTypes from 'prop-types';
import {categories} from '../../../config/service-config';
import {tableConfig} from '../../../config/table-config';
import Progress from '../helpers/progress';
import {addAlias, deleteAlias} from '../../redux/actions/genre-actions';
import ScrollToTop from '../helpers/scroll-to-top';

const ALIASES_KEY = 'ALIASES';

// eslint-disable-next-line no-unused-vars
const useStyles = makeStyles(theme => ({
  categories: {height: 35, padding: 10, margin: 10, minWidth: 193},
  autoCompleteList: {width: 300, paddingLeft: 20, paddingTop: 20},
  restoreButton: {
    color: '#757575',
    borderRadius: '100%',
    minWidth: 48,
    minHeight: 48,
  },
  selectButton: {
    marginRight: 10,
  },
  dialogDeleteForm: {
    color: '#ec0d0d',
  },
  deleteTable: {
    width: '100%',
    textAlign: 'center',
    verticalAlign: 'middle',
    border: '1px solid #ddd',
    borderCollapse: 'collapse',
    overflow: 'auto',
    '& th': {
      backgroundColor: '#3d659b',
      color: 'white',
    },
    '& tr:nth-child(even)': {backgroundColor: '#f2f2f2'},
  },
  categoryError: {color: 'red'},
}));

const Genre = ({assetCategory, isSelect = false, onGenresSelection}) => {
  const aliasesService = useService(ALIASES_KEY);
  const aliasesContent = aliasesService?.serviceContent?.aliases;
  const classes = useStyles();
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [genres, setGenres] = useState([]);
  const [isDelete, setIsDelete] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [aliasToDelete, setAliasToDelete] = useState([]);
  const [aliasToAdd, setAliasToAdd] = useState(null);
  const [disableAdd, setDisableAdd] = useState(false);
  const [aliasesTable, setAliasesTable] = useState([]);
  const [count, setCount] = useState(null);
  const [pagination, setPagination] = useState(true);
  const [categoryError, setCategoryError] = useState(null);
  const {headerConfig, columns} = tableConfig[ALIASES_KEY];
  const fetching = useSelector(state => state?.service[ALIASES_KEY]?.fetching);
  const updateState = useSelector(state => state?.genre);
  const dispatch = useDispatch();
  const rippleRoles = GetRippleRoles();

  useEffect(() => {
    if (areRolesAuthorized(rippleRoles, 'GENRE', ['CREATE', 'DELETE', 'READ'])) {
      aliasesService.registerService();
      return () => {
        aliasesService.unregisterService();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (updateState && Object.keys(updateState).length) {
      const {error} = updateState;
      if (!error) {
        aliasesService.registerService();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateState]);

  useEffect(() => {
    setSelectedGenre(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  useEffect(() => {
    setAliasesTable(aliases.filter(a => a.genreName === selectedGenre));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGenre]);

  useEffect(() => {
    const aliasesLocal =
      aliasesContent && aliasesContent.length ? aliasesContent?.filter(a => a?.category === selectedCategory) : [];
    setAliases(aliasesLocal);
    setAliasesTable(aliasesLocal.filter(a => a.genreName === selectedGenre));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aliasesContent]);

  const setAliasesGenres = categoryAG => {
    setSelectedCategory(categoryAG);
    const aliasesLocal =
      aliasesContent && aliasesContent.length ? aliasesContent?.filter(a => a?.category === categoryAG) : [];
    setAliases(aliasesLocal);
    const genresLocal = [...new Set(aliasesLocal.map(a => a.genreName))].filter(item => item).sort();
    setGenres(genresLocal);
  };

  const tableOptions = () => ({
    selectableRows: areRolesAuthorized(rippleRoles, 'GENRE', ['DELETE']) ? 'multi' : 'none',
    expandableRows: false,
    expandableRowsOnClick: false,
    rowsPerPage: 10,
    rowsPerPageOptions: [10, 25],
    rowHover: true,
    count,
    search: true,
    filter: false,
    viewColumns: false,
    responsive: 'standard',
    selectableRowsHeader: true,
    selectableRowsOnClick: true,
    selectToolbarPlacement: 'above',
    print: false,
    download: false,
    sortOrder: {aliasName: 'asc'},
    elevation: 2,
    pagination,
    // eslint-disable-next-line no-unused-vars
    onTableChange: (action, tableState) => {
      const records = tableState?.displayData?.length;
      setPagination(records !== 0);
      setCount(records);
    },
    customToolbar: () => (
      <Button
        className={classes.restoreButton}
        disabled={!areRolesAuthorized(rippleRoles, 'GENRE', ['CREATE'])}
        title="Create Alias"
        variant="text"
        onClick={() => setIsAdd(true)}
      >
        <Add />
      </Button>
    ),
    customToolbarSelect: selectedRows => {
      const {data} = selectedRows;
      const deletable = data.map(d => aliasesTable[d.dataIndex]);
      return (
        <>
          {!isSelect && deletable?.length ? (
            <Button
              className={classes.restoreButton}
              title="Delete Alias"
              variant="text"
              onClick={() => {
                setAliasToDelete(deletable);
                setIsDelete(true);
              }}
            >
              <Delete />
            </Button>
          ) : null}
          {isSelect ? (
            <Button
              className={classes.selectButton}
              color="primary"
              title="Select Alias"
              variant="outlined"
              onClick={() => {
                const aliasesSelected = selectedRows?.data?.map(s => aliasesTable[s?.dataIndex]);
                const {genreName, genreStrValue} = aliasesSelected?.length
                  ? aliasesSelected[0]
                  : {genreName: null, genreStrValue: null};
                onGenresSelection({
                  category: categories?.find(c => c.code === selectedCategory)?.short,
                  genre: {name: genreName, strValue: genreStrValue},
                  aliases: aliasesSelected,
                });
              }}
            >
              <Select />
              Update
            </Button>
          ) : null}
        </>
      );
    },
  });

  const disableAlerts = () => {
    setDisableAdd(false);
    setAliasToAdd(null);
    setIsAdd(false);
  };

  const deleteDialog = () => (
    <Dialog fullWidth={true} maxWidth="xs" open={isDelete} onClose={() => setIsDelete(false)}>
      <DialogTitle className={classes.dialogDeleteForm}>
        {`Do you want to delete ${aliasToDelete.length === 1 ? 'this Alias' : 'these Aliases'}?`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <table border={2} className={classes.deleteTable}>
            <tr>
              <th>ID</th>
              <th>Alias Name</th>
            </tr>
            {aliasToDelete.map((a, i) => (
              <tr key={i}>
                <td>{a?.aliasID}</td>
                <td>{a?.aliasName}</td>
              </tr>
            ))}
          </table>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={() => setIsDelete(false)}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={!aliasToDelete || !aliasToDelete?.length}
          onClick={() => {
            setIsDelete(false);
            aliasToDelete.forEach(a => dispatch(deleteAlias(a.aliasID)));
          }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  const addDialog = () => (
    <Dialog fullWidth={true} maxWidth="xs" open={isAdd} onClose={() => disableAlerts()}>
      <DialogTitle>
        {`Create Alias (${categories.find(c => c.code === selectedCategory)?.name}, ${selectedGenre})`}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <TextField
            autoFocus={true}
            error={disableAdd && aliasToAdd.length > 0}
            label={disableAdd && aliasToAdd.length > 0 ? 'Alias already exists' : 'Enter Alias value'}
            style={{width: '100%'}}
            onChange={e => {
              const typed = e?.target?.value;
              const alreadyExists = aliases?.filter(a => a?.aliasName.trim() === typed.trim())?.length;
              setDisableAdd(alreadyExists > 0);
              setAliasToAdd(typed);
            }}
          />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          onClick={() => {
            disableAlerts();
          }}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={disableAdd || aliasToAdd === null || !aliasToAdd?.length}
          onClick={() => {
            const genreIndex = aliasesTable?.length ? aliasesTable[0]?.genre : null;
            disableAlerts();
            dispatch(addAlias({category: selectedCategory, genre: genreIndex, aliasName: aliasToAdd}));
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Grid alignItems="center" container={true} direction="column" justifyContent="center">
      {categoryError ? <span className={classes.categoryError}>Current Asset category will be replaced!</span> : null}
      <Grid alignItems="center" container={true} direction="row" justifyContent="center">
        <Autocomplete
          className={classes.autoCompleteList}
          getOptionLabel={option => option.name}
          options={categories.filter(c => c.code !== 0)}
          renderInput={params => <TextField {...params} label="Select Category" variant="outlined" />}
          /* eslint-disable-next-line no-unused-vars */
          onChange={(event, newValue) => {
            setAliases([]);
            setGenres([]);
            if (assetCategory && newValue?.code) {
              const tempCategoryCode = categories.find(c => c.short.toLowerCase() === assetCategory.toLowerCase())
                ?.code;
              setCategoryError(newValue.code !== tempCategoryCode ? true : null);
            } else {
              setCategoryError(null);
            }
            if (newValue?.code) {
              setAliasesGenres(newValue.code);
            }
          }}
        />

        {genres && genres.length ? (
          <Autocomplete
            className={classes.autoCompleteList}
            getOptionLabel={option => option}
            options={genres}
            renderInput={params => <TextField {...params} label="Select Genre" variant="outlined" />}
            value={selectedGenre || null}
            /* eslint-disable-next-line no-unused-vars */
            onChange={(event, newValue) => setSelectedGenre(newValue)}
          />
        ) : null}
      </Grid>
      <Grid alignItems="center" container={true} direction="row" justifyContent="center" style={{padding: 20}}>
        {aliases?.length && selectedGenre ? (
          <MUIDataTable
            columns={columns}
            data={aliasesTable}
            headerConfig={headerConfig}
            idKey="name"
            itemLabel="Alias"
            options={tableOptions()}
            style={{width: 580}}
            title="Aliases"
          />
        ) : null}
      </Grid>
      {fetching && <Progress title="Aliases" />}
      {deleteDialog()}
      {addDialog()}
      <ScrollToTop />
    </Grid>
  );
};

Genre.propTypes = {
  assetCategory: PropTypes.string,
  isSelect: PropTypes.bool,
  onGenresSelection: PropTypes.func,
};

export default Genre;
